import { Browser } from '@capacitor/browser'
import { Alert, Button, Stack } from '@mantine/core'
import {
  SignInForm as CoreSignInForm,
  useAuthSignIn,
  useNativeOAuthCallback
} from '@packages/core'
import { usePasskeys, useSupabase } from '@packages/supabase'
import { H3 } from '@packages/ui'
import { IconKey } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

export const SignInForm = () => {
  const { supabase } = useSupabase()
  const { t } = useTranslation()
  const auth = useAuthSignIn({
    openOAuthBrowser: (url) => Browser.open({ url })
  })

  const {
    isSupported: passkeySupported,
    signInWithPasskey,
    loading: passkeyLoading,
    error: passkeyError
  } = usePasskeys()

  useNativeOAuthCallback(supabase, {
    onError: (message) => auth.actions.setError(message),
    closeBrowser: () => Browser.close()
  })

  const handlePasskeySignIn = async () => {
    try {
      await signInWithPasskey(
        auth.state.email ? { email: auth.state.email } : undefined
      )
    } catch {
      // surfaced via passkeyError
    }
  }

  return (
    <CoreSignInForm
      auth={auth}
      variant="default"
      slots={{
        panel: (children) => (
          <Stack c="white" m="md">
            {children}
          </Stack>
        ),
        heading: () => (
          <H3 className="text-2xl font-light" data-testid="signin-heading">
            {t('auth.widgets.sign_in.label', { defaultValue: 'Login' })}
          </H3>
        ),
        passkey: passkeySupported
          ? () => (
              <>
                <Button
                  leftSection={<IconKey size={16} />}
                  loading={passkeyLoading}
                  onClick={handlePasskeySignIn}
                  data-testid="passkey-login-button"
                >
                  {t('auth.passkey.sign_in.label', {
                    defaultValue: 'Sign in with passkey'
                  })}
                </Button>
                {passkeyError && passkeyError.code !== 'canceled' && (
                  <Alert color="red" data-testid="passkey-login-error">
                    {t(`auth.passkey.errors.${passkeyError.code}`, {
                      defaultValue: passkeyError.message
                    })}
                  </Alert>
                )}
              </>
            )
          : false
      }}
    />
  )
}
